import { getStoredTokens, clearTokens, refreshSession, storeTokens } from "./auth";
import { API_BASE_URL } from "./env";
import { formatErrorDetail } from "./errors";

const BASE = API_BASE_URL;

let refreshPromise: Promise<string | null> | null = null;

async function doRefresh(): Promise<string | null> {
  const result = await refreshSession();
  return result?.id_token ?? null;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  let tokens = getStoredTokens();
  if (!tokens?.id_token) {
    clearTokens();
    window.location.href = "/login";
    throw new Error("No auth token");
  }

  const doFetch = (token: string) =>
    fetch(`${BASE}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

  let resp = await doFetch(tokens.id_token);

  if (resp.status === 401 && tokens.refresh_token) {
    if (!refreshPromise) {
      refreshPromise = doRefresh().finally(() => { refreshPromise = null; });
    }
    const newToken = await refreshPromise;
    if (newToken) {
      resp = await doFetch(newToken);
    }
  }

  if (resp.status === 401) {
    clearTokens();
    window.location.href = "/login";
    throw new Error("Session expired");
  }

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ detail: "Request failed" }));
    const error = new Error(
      formatErrorDetail(err.detail, `Error ${resp.status}`)
    );
    (error as any).status = resp.status;
    throw error;
  }

  if (resp.status === 204) return undefined as T;
  return resp.json();
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PUT", body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

// ── Types matching backend schemas ──

export interface DashboardMetrics {
  total_leads: number;
  qualified_leads: number;
  active_blueprints: number;
  conversion_rate: number;
}

export interface ActivityItem {
  id: string;
  user_name: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  entity_name: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

export interface DashboardResponse {
  metrics: DashboardMetrics;
  recent_activities: ActivityItem[];
}

export interface BlueprintMember {
  id: string;
  user_id: string;
  user_name: string | null;
  user_email: string | null;
  role: string;
  created_at: string;
}

export interface BlueprintSource {
  id: string;
  source_id: string;
  source_name: string | null;
  source_type: string | null;
  source_status: string | null;
  created_at: string;
}

export interface Blueprint {
  id: string;
  name: string;
  description: string | null;
  requirement_text: string | null;
  targeting_strategy: Record<string, unknown> | null;
  status: string;
  config: Record<string, unknown> | null;
  lead_count: number;
  is_archived: boolean;
  archived_at: string | null;
  created_by: string;
  creator_name: string | null;
  members: BlueprintMember[];
  sources: BlueprintSource[];
  created_at: string;
  updated_at: string;
}

export interface BlueprintListItem {
  id: string;
  name: string;
  description: string | null;
  status: string;
  lead_count: number;
  is_archived: boolean;
  archived_at: string | null;
  created_by: string;
  creator_name: string | null;
  created_at: string;
}

export interface Lead {
  id: string;
  blueprint_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  email_verified: boolean;
  phone: string | null;
  job_title: string | null;
  linkedin_url: string | null;
  company_id: string | null;
  company_name: string | null;
  company_domain: string | null;
  source_type: string | null;
  score: number;
  score_breakdown: Record<string, unknown> | null;
  status: string;
  dead_reason: string | null;
  claimed_by: string | null;
  claimer_name: string | null;
  claimed_at: string | null;
  next_follow_up_at: string | null;
  last_activity_at: string | null;
  created_at: string;
}

export interface LeadListResponse {
  leads: Lead[];
  total: number;
  page: number;
  page_size: number;
}

export interface DataSource {
  id: string;
  name: string;
  type: string;
  status: string;
  config: Record<string, unknown> | null;
  last_sync_at: string | null;
  total_leads_sourced: number;
  created_at: string;
}

export interface UserItem {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  organization_id: string;
}

// ── Superadmin (admin management page) ──

export interface SuperadminUserItem {
  id: string;
  email: string;
  full_name: string;
  role: string;
  is_active: boolean;
  organization_id: string;
  created_at: string;
  last_login_at: string | null;
  blueprint_count: number;
  credits_used_30d: number;
}

export interface UserBlueprintAccess {
  blueprint_id: string;
  name: string;
  description: string | null;
  status: string;
  lead_count: number;
  is_archived: boolean;
  role: "owner" | "collaborator" | "viewer" | "superadmin";
  member_id: string | null;
  added_at: string | null;
}

export interface ConnectorCredit {
  key: string;
  name: string;
  description: string | null;
  icon: string;
  configured: boolean;
  status: "connected" | "error" | "not_configured" | string;
  message: string | null;
  remaining_credits: number | null;
  remaining_message: string | null;
  used_30d: number | null;
  used_today: number | null;
  reveal_count_30d: number | null;
  details: Record<string, unknown>;
}

export interface ConnectorCreditsResponse {
  connectors: ConnectorCredit[];
  generated_at: string;
}

export interface LeadStatusBreakdown {
  unclaimed: number;
  claimed: number;
  contacted: number;
  qualified: number;
  won: number;
  dead: number;
}

export interface MemberPerformance {
  user_id: string;
  user_name: string;
  leads_claimed: number;
  leads_contacted: number;
  leads_qualified: number;
  leads_won: number;
  leads_dead: number;
}

export interface FollowUpSummary {
  upcoming: number;
  overdue: number;
  completed: number;
}

export interface OverviewActivity {
  id: string;
  user_name: string;
  action: string;
  entity_type: string;
  entity_id: string | null;
  entity_name: string | null;
  details: Record<string, unknown> | null;
  created_at: string;
}

export interface BlueprintOverview {
  total_leads: number;
  leads_claimed: number;
  leads_qualified: number;
  conversion_rate: number;
  avg_lead_score: number;
  status_breakdown: LeadStatusBreakdown;
  member_performance: MemberPerformance[];
  follow_ups: FollowUpSummary;
  recent_activities: OverviewActivity[];
}

export interface PipelineRun {
  id: string;
  blueprint_id: string;
  run_number: number;
  status: string;
  strategy_snapshot: Record<string, unknown> | null;
  source_plan: SourcePlan | null;
  progress: Record<string, unknown>;
  result_summary: Record<string, unknown> | null;
  error_message: string | null;
  is_cancelled: boolean;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  triggered_by_name: string | null;
}

export interface SourcePlanEntry {
  source_id: string;
  source_name: string;
  source_type: string;
  matched_criteria: string[];
  unmatched_criteria: string[];
  search_params: Record<string, unknown>;
  estimated_results: number;
}

export interface SourcePlan {
  sources: SourcePlanEntry[];
  total_estimated_leads: number;
  warnings: string[];
  recommendations: string[];
}

export interface GatherPlan {
  primary_source: string;
  search_queries: string[];
  geographic_targets: string[];
  business_type_filters: string[];
  estimated_companies: number;
  rationale: string;
}

export interface StrategyResponse {
  targeting_strategy: Record<string, unknown> | null;
  source_plan: GatherPlan | SourcePlan | null;
  status: string;
}

export interface ICP {
  titles: string[];
  seniority_levels: string[];
  industries: string[];
  departments: string[];
  company_size_ranges: string[];
  geographies: string[];
  keywords: string[];
  business_types: string[];
  exclusions: { titles: string[]; industries: string[]; keywords?: string[]; business_types?: string[] };
}

export interface FollowUp {
  id: string;
  lead_id: string;
  created_by: string;
  creator_name: string | null;
  type: string;
  title: string;
  description: string | null;
  scheduled_date: string;
  scheduled_time: string | null;
  status: string;
  outcome: string | null;
  notes: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface Note {
  id: string;
  lead_id: string;
  user_id: string;
  author_name: string | null;
  content: string;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
}

// ── Company types ──

export interface CompanyContact {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  job_title: string | null;
  linkedin_url: string | null;
  status: string;
  score: number;
  claimed_by: string | null;
  claimer_name: string | null;
}

export interface CompanyDetail {
  id: string;
  name: string;
  domain: string | null;
  status: string;
  industry: string | null;
  employee_count: number | null;
  location: string | null;
  country: string | null;
  linkedin_url: string | null;
  website: string | null;
  description: string | null;
  address: string | null;
  phone: string | null;
  category: string | null;
  rating: number | null;
  tech_stack: string[] | null;
  social_links: Record<string, string> | null;
  completeness_score: number;
  enrichment_data: Record<string, unknown> | null;
  blueprint_id: string | null;
  blueprint_name: string | null;
  contact_count: number;
  contacts: CompanyContact[];
  created_at: string;
  updated_at: string;
}

export interface CompanyListItem {
  id: string;
  name: string;
  domain: string | null;
  status: string;
  industry: string | null;
  location: string | null;
  employee_count: number | null;
  completeness_score: number;
  blueprint_id: string | null;
  blueprint_name: string | null;
  contact_count: number;
  best_score: number;
  created_at: string;
}

export interface CompanyListResponse {
  companies: CompanyListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface CompanyNote {
  id: string;
  content: string;
  author_name: string | null;
  created_at: string;
}

export interface CompanyActivityItem {
  id: string;
  type: "activity" | "note";
  action: string;
  actor_name: string | null;
  details: Record<string, unknown> | null;
  content: string | null;
  entity_type: string | null;
  entity_name: string | null;
  created_at: string;
}

export interface SampleContact {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  job_title: string | null;
  linkedin_url: string | null;
  has_email_available: boolean;
  has_phone_available: boolean;
}

export interface SampleCompany {
  id: string;
  name: string;
  domain: string | null;
  industry: string | null;
  location: string | null;
  website: string | null;
  description: string | null;
  category: string | null;
  employee_count: number | null;
  phone: string | null;
  linkedin_url: string | null;
  completeness_score: number;
  contacts: SampleContact[];
}

export interface SampleLead {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  job_title: string | null;
  company_name: string | null;
  linkedin_url: string | null;
}

export interface SampleReviewData {
  companies: SampleCompany[];
  leads: SampleLead[];
  total_companies: number;
  total_leads: number;
  source_type: string;
  strategy_snapshot: Record<string, unknown> | null;
  is_enriched: boolean;
  gather_warnings: string[];
}

// ── People Search ──

export interface PeopleSearchRequest {
  // Quick inputs
  name?: string;
  company?: string;
  q?: string;
  linkedin_url?: string;
  // Person
  person_titles?: string[];
  include_similar_titles?: boolean;
  person_seniorities?: string[];
  person_locations?: string[];
  // Email
  contact_email_status?: string[];
  // Company
  organization_locations?: string[];
  organization_domains?: string[];
  organization_num_employees_ranges?: string[];
  revenue_range_min?: number;
  revenue_range_max?: number;
  // Technology
  currently_using_any_of_technology_uids?: string[];
  currently_using_all_of_technology_uids?: string[];
  currently_not_using_any_of_technology_uids?: string[];
  // Hiring signals
  q_organization_job_titles?: string[];
  organization_job_locations?: string[];
  organization_num_jobs_min?: number;
  organization_num_jobs_max?: number;
  organization_job_posted_at_min?: string;
  organization_job_posted_at_max?: string;
  // Pagination
  page?: number;
  per_page?: number;
}

export interface PeopleSearchResultItem {
  apollo_person_id: string | null;
  first_name: string | null;
  last_name: string | null;
  last_name_obfuscated: string | null;
  full_name: string | null;
  title: string | null;
  seniority: string | null;
  headline: string | null;
  photo_url: string | null;
  linkedin_url: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  company_name: string | null;
  company_domain: string | null;
  company_industry: string | null;
  company_employee_count: number | null;
  has_email_on_file: boolean;
  has_phone_on_file: boolean;
}

export interface PeopleSearchAttempt {
  strategy: string;
  params: Record<string, unknown>;
  result_count: number;
  total_entries: number | null;
}

export interface PeopleSearchResponse {
  results: PeopleSearchResultItem[];
  page: number;
  per_page: number;
  total_entries: number;
  total_pages: number;
  strategy: string;
  query_used: Record<string, unknown>;
  attempts: PeopleSearchAttempt[];
}

export interface RevealRequest {
  apollo_person_id?: string | null;
  linkedin_url?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  domain?: string | null;
  organization_name?: string | null;
  email?: string | null;
}

export interface RevealedPerson {
  apollo_person_id: string | null;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  title: string | null;
  seniority: string | null;
  headline?: string | null;
  photo_url: string | null;
  linkedin_url: string | null;
  email: string | null;
  email_verified: boolean;
  email_status: string | null;
  phone: string | null;
  phone_type: string | null;
  all_phones: Array<Record<string, unknown>>;
  city: string | null;
  state: string | null;
  country: string | null;
  company_name: string | null;
  company_domain: string | null;
  company_industry: string | null;
  company_employee_count: number | null;
}

export interface RevealResponse {
  success: boolean;
  person: RevealedPerson | null;
  revealed: string[];
  cached: boolean;
  pending: boolean;
  credits_estimated: number;
  message: string;
  activity_id: string | null;
  revealed_at: string | null;
  cache_key: string | null;
}

export interface RevealPhoneStatusResponse {
  pending: boolean;
  delivery_status: "ok" | "no_phone_on_file" | null;
  person: RevealedPerson;
  revealed: string[];
  activity_id: string;
  revealed_at?: string | null;
}

export interface CreditsStatus {
  configured: boolean;
  status?: string;
  message?: string;
  email_reveal_credits?: number;
  phone_reveal_credits?: number;
  phone_webhook_configured?: boolean;
  note?: string;
}
